import React, { useEffect, useState } from 'react';
import {
  collection, query, where, orderBy, limit as fbLimit, onSnapshot,
} from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { ACCENT_TOKENS } from '../resources/registry';
import { FORMS, getFormById, FALLBACK_CHAPTER_NAME_TO_SLUG } from './formsRegistry';
import './forms-library.css';

function fmtDate(ts) {
  if (!ts) return '';
  const date = ts.toDate ? ts.toDate() : (ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts));
  try {
    return date.toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return '';
  }
}

// Admin Panel → Form Responses. Every completed Forms Library submission
// across both forms, newest first — chapter directors see their own chapter,
// superAdmins can filter across all of them. Lives here (not in the Resources
// Forms Library) so directors have one place to find responses.
export default function FormResponses({ user, isSuperAdmin }) {
  const userChapter = user?.chapter || '';
  const [typeFilter, setTypeFilter] = useState('all');
  const [chapterFilter, setChapterFilter] = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    const constraints = [orderBy('submittedAt', 'desc'), fbLimit(200)];
    if (typeFilter !== 'all') {
      constraints.unshift(where('formType', '==', typeFilter));
    }
    if (!isSuperAdmin) {
      // Firestore rules scope reads to the LP's own chapter — mirror that here.
      if (!userChapter) {
        setSubmissions([]);
        setLoading(false);
        return undefined;
      }
      constraints.unshift(where('chapter', '==', userChapter));
    } else if (chapterFilter) {
      constraints.unshift(where('chapter', '==', chapterFilter));
    }
    const q = query(collection(db, 'formLibrarySubmissions'), ...constraints);
    const unsub = onSnapshot(
      q,
      (snap) => {
        setSubmissions(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('formLibrarySubmissions snapshot error:', err);
        setError(err.message || 'Failed to load responses.');
        setLoading(false);
      },
    );
    return unsub;
  }, [typeFilter, chapterFilter, userChapter, isSuperAdmin]);

  async function handleDownloadPdf(submission) {
    const formMeta = getFormById(submission.formType)
      || { id: submission.formType, title: submission.formType, number: '', category: '' };
    try {
      const [rendererMod, pdfMod] = await Promise.all([
        import('@react-pdf/renderer'),
        import('./pdf/FormSubmissionPDF'),
      ]);
      const FormSubmissionPDF = pdfMod.default;
      const blob = await rendererMod.pdf(
        <FormSubmissionPDF submission={submission} formMeta={formMeta} />,
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const filenameBase = (submission.name || submission.fullName || 'submission')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      a.download = `${formMeta.id}-${filenameBase || 'submission'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('PDF generation failed', err);
      alert(`PDF error: ${err?.message || 'Unknown error'}`);
    }
  }

  return (
    <div>
      <div className="filter-bar filter-bar--one-row" style={{ marginBottom: 20 }}>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          aria-label="Filter by form"
        >
          <option value="all">All forms</option>
          {FORMS.map((f) => (
            <option key={f.id} value={f.id}>{f.number} — {f.title}</option>
          ))}
        </select>
        {isSuperAdmin && (
          <select
            value={chapterFilter}
            onChange={(e) => setChapterFilter(e.target.value)}
            aria-label="Filter by chapter"
          >
            <option value="">All chapters</option>
            {Object.keys(FALLBACK_CHAPTER_NAME_TO_SLUG).map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        )}
        <span className="filter-spacer" />
        {!loading && !error && (
          <span className="forms-library__submission-meta" style={{ margin: 0 }}>
            {submissions.length} response{submissions.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {loading && <div className="forms-library__empty">Loading responses…</div>}
      {!loading && error && (
        <div className="forms-library__msg forms-library__msg--err">{error}</div>
      )}
      {!loading && !error && submissions.length === 0 && (
        <div className="forms-library__empty">
          No responses yet. Completed forms from the Resources → Forms Library land here automatically.
        </div>
      )}

      {!loading && !error && submissions.length > 0 && (
        <div className="forms-library__submissions">
          {submissions.map((s) => {
            const meta = getFormById(s.formType);
            const accent = ACCENT_TOKENS[meta?.accent] || ACCENT_TOKENS.magenta;
            const thumbUrl = s.headshotUrl || s.photoUrl;
            const displayName = s.name || s.fullName || s.email || 'Submission';
            const detail = s.formType === 'lp-onboarding'
              ? (s.bio || '').slice(0, 140) + ((s.bio || '').length > 140 ? '…' : '')
              : [s.addressCity, s.addressState].filter(Boolean).join(', ');
            return (
              <div key={s.id} className="forms-library__submission">
                {thumbUrl ? (
                  <img src={thumbUrl} alt="" className="forms-library__submission-thumb" />
                ) : (
                  <div className="forms-library__submission-thumb" aria-hidden="true" />
                )}
                <div className="forms-library__submission-body">
                  <p className="forms-library__submission-name">{displayName}</p>
                  <p className="forms-library__submission-meta">
                    <span
                      className="forms-library__submission-tag"
                      style={{ background: accent.bg, color: accent.text }}
                    >
                      {meta ? `${meta.number} ${meta.title}` : s.formType}
                    </span>
                    {s.email} · {s.chapter} · {fmtDate(s.submittedAt)}
                  </p>
                  {detail && <p className="forms-library__submission-detail">{detail}</p>}
                </div>
                <div className="forms-library__submission-actions">
                  <button
                    type="button"
                    className="forms-library__btn forms-library__btn--ghost"
                    onClick={() => handleDownloadPdf(s)}
                  >
                    ↓ PDF
                  </button>
                  {thumbUrl && (
                    <a
                      href={thumbUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="forms-library__btn forms-library__btn--ghost"
                      style={{ textDecoration: 'none', textAlign: 'center' }}
                    >
                      Photo
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
